import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { VideoInventory, inventoryTotal } from "@/components/VideoInventory";
import { PrintableInventory } from "@/components/PrintableInventory";
import { useAuth } from "@/context/AuthContext";
import { getGuestId, loadDraft, saveDraft } from "@/lib/guest";
import { Video, Printer, Gavel, BadgeCheck, ArrowRight, Sparkles, Ruler, Lock } from "lucide-react";
import { MONT } from "@/pages/Movers";
import { ContactEmail } from "@/components/ContactEmail";

const SIGNUP_URL = "/auth?mode=signup&role=customer&next=/post-job";

const PERKS = [
  { icon: Video, title: "Film each room", text: "Our AI spots every item for you. Or type them in yourself." },
  { icon: Ruler, title: "See your m³", text: "Total cubic metres worked out automatically, room by room." },
  { icon: Printer, title: "Print it", text: "A tidy, itemised list you can keep, share or print." },
];

function PostMoveCta({ total, onPost, onPrint }) {
  return (
    <Card className="relative overflow-hidden bg-asphalt text-white p-6 sm:p-8 border-0 print:hidden" data-testid="inventory-post-cta">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-kinetic/30 blur-3xl" />
      <div className="relative space-y-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="inventory-free-badge">
          <BadgeCheck className="h-3.5 w-3.5" /> Costs you nothing
        </span>
        <h2 className="text-2xl sm:text-3xl font-black leading-tight" style={MONT}>What will moving {total ? `${total} m³` : "this"} cost? Let removalists tell you.</h2>
        <p className="text-white/75 max-w-xl text-sm sm:text-base">Post your move on MoveTender with this inventory and verified removalists compete with their best price. Free sign-up, no fees, no obligation to accept a bid.</p>
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <Button size="lg" onClick={onPost} data-testid="inventory-post-move-btn" className="bg-kinetic hover:bg-kinetic-hover text-white gap-2 h-12 px-6 font-bold shadow-lg shadow-kinetic/30 hover:-translate-y-0.5 transition-transform">
            <Gavel className="h-5 w-5" /> Post my move free <ArrowRight className="h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" onClick={onPrint} data-testid="inventory-print-btn" className="h-12 gap-2 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
            <Printer className="h-5 w-5" /> Print inventory
          </Button>
        </div>
        <p className="text-xs text-white/50 flex items-center gap-1.5"><Lock className="h-3 w-3" /> Your inventory is saved on this device and carries straight into your job post.</p>
      </div>
    </Card>
  );
}

export default function InventoryTool() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inventory, setInventory] = useState(() => loadDraft());
  useEffect(() => { getGuestId(); }, []);
  const update = (next) => { setInventory(next); saveDraft(next); };
  const rooms = inventory?.rooms || [];
  const hasItems = rooms.some((r) => r.items.length);
  const post = () => navigate(user?.role === "customer" ? "/post-job" : SIGNUP_URL);

  return (
    <div className="min-h-screen bg-background text-foreground print:bg-white">
      <div className="print:hidden">
        <header className="max-w-6xl mx-auto px-4 sm:px-6 h-20 sm:h-24 flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate("/")} data-testid="inventory-brand-link"><BrandLogo size="md" /></button>
          <div className="flex items-center gap-1 sm:gap-2">
            {user === false && <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="inventory-login-btn" className="px-2 sm:px-4">Log in</Button>}
            <Button onClick={post} data-testid="inventory-header-post-btn" className="bg-kinetic hover:bg-kinetic-hover text-white px-3 sm:px-4">Post my move</Button>
          </div>
        </header>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-end">
          <div className="animate-fade-up">
            <p className="font-mono text-xs uppercase tracking-widest text-kinetic mb-3 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Free moving inventory tool</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-brandgrey leading-[1.05]" style={MONT}>
              Know exactly what you're <span className="text-kinetic">moving.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl">Film each room and our AI builds an itemised inventory with cubic metres in seconds. No sign-up needed. Print it, or post your move to get prices from removalists.</p>
          </div>
          <ul className="grid sm:grid-cols-3 lg:grid-cols-1 gap-3 animate-fade-up" style={{ animationDelay: "120ms" }}>
            {PERKS.map((p) => (
              <li key={p.title} className="flex gap-3 items-start rounded-xl border border-border bg-card p-4">
                <span className="h-9 w-9 rounded-lg bg-kinetic-light flex items-center justify-center shrink-0"><p.icon className="h-4 w-4 text-kinetic" /></span>
                <span><span className="block font-bold text-sm" style={MONT}>{p.title}</span><span className="text-xs text-muted-foreground">{p.text}</span></span>
              </li>
            ))}
          </ul>
        </section>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 space-y-8">
          <Card className="p-4 sm:p-6" data-testid="inventory-tool-card">
            <VideoInventory value={inventory} onChange={update} />
          </Card>
          {hasItems && <PostMoveCta total={inventoryTotal(rooms)} onPost={post} onPrint={() => window.print()} />}
          {!hasItems && (
            <p className="text-center text-sm text-muted-foreground" data-testid="inventory-free-note">
              <BadgeCheck className="inline h-4 w-4 text-emerald-600 mr-1 -mt-0.5" />
              MoveTender is free for customers: post your move and removalists bid for it.
            </p>
          )}
        </main>
        <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">MoveTender — Australia's Moving Marketplace · <ContactEmail /></footer>
      </div>
      {hasItems && <PrintableInventory rooms={rooms} />}
    </div>
  );
}
